// Licence numbers printed under the company header on quote and invoice PDFs — the ONE owner
// (operator approved 2026-10-29, docs/investigations/2026-10-29-settings-company-profile.md Q4).
// ServiceTitan prints the business unit's licence number at the top of every invoice; several
// states require the licence number on bids and contracts.
//
// Source: companies.licenses, the contractor's own list (Settings > Company Profile > Licenses),
// each { id, type, number, state, expiry_date, description }. Printed as the contractor's own
// statement (P26): type, number and state only — no "verified"/"licensed" wording, no badge.
// `description` is never printed (free text).
//
// Expired licences are left out: expiry_date is a calendar date ('YYYY-MM-DD'), compared as a
// string with the company's today key (pass companyDateKey() from the app). No expiry date =
// shown. A licence the contractor unticked "Show on quotes and invoices" for
// (show_on_documents === false) is left out. No licences = [] and the document prints nothing.

import { normalizeStateToCode } from './usStates';

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @param {Array|string|null} licenses  companies.licenses
 * @param {string} todayKey            the company's today, 'YYYY-MM-DD'
 * @returns {string[]}                  one plain-text line per current licence (caller escapes)
 */
export function documentLicenseLines(licenses, todayKey) {
  let list = licenses;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch (e) { return []; }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((l) => l && typeof l === 'object' && String(l.number || '').trim())
    // Per-licence "Show on quotes and invoices" (LicensesCard); missing = shown.
    .filter((l) => l.show_on_documents !== false)
    .filter((l) => {
      const expiry = String(l.expiry_date || '').slice(0, 10);
      if (!DATE_KEY.test(expiry) || !DATE_KEY.test(String(todayKey || ''))) return true;
      return expiry >= todayKey;
    })
    .map((l) => {
      const type = String(l.type || '').trim();
      const state = normalizeStateToCode(l.state) || String(l.state || '').trim();
      // "Electrical License #…", "EPA Certification #…", "Business License #…", "License #…".
      let label = 'License #';
      if (type && type !== 'Other') {
        label = /(License|Certificate|Certification)$/i.test(type) ? `${type} #` : `${type} License #`;
      }
      return `${label}${String(l.number).trim()}${state ? ` (${state})` : ''}`;
    });
}
